import { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchDocuments,
  fetchFolders,
  docTypeMeta,
  type DocDocument,
  type DocFolder,
} from '@/lib/documents';
import { addRoomFile, createShare, shareAbsoluteUrl, type ShareItem } from '@/lib/zcall';
import { md5 } from '@/lib/md5';

interface Props {
  userId: number;
  userName: string;
  title: string;
  /** Si fourni, le partage est aussi ajouté aux fichiers de la réunion ZCall. */
  roomId?: string | null;
  /** Dossier présélectionné (partage d'un dossier précis). */
  initialFolderId?: number | null;
  /** Fichiers (documents) présélectionnés (partage de fichiers précis). */
  initialDocIds?: number[];
  /**
   * `folder` / `doc` : le partage est VERROUILLÉ sur l'élément cliqué (aucune
   * autre sélection possible). `select` : choix libre dans la bibliothèque.
   */
  mode?: 'select' | 'folder' | 'doc';
  onClose: () => void;
}

interface ExcelSheetLike {
  cells?: Record<string, string>;
  rows?: number;
  cols?: number;
}

function esc(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Rend une feuille de calcul en tableau HTML (aperçu lisible). */
function excelToHtml(content: Record<string, unknown>): string {
  const sheets = (content.sheets as ExcelSheetLike[] | undefined) || [];
  const sheet: ExcelSheetLike = sheets[0] || {
    cells: content.cells as Record<string, string> | undefined,
    rows: content.rows as number | undefined,
    cols: content.cols as number | undefined,
  };
  const cells = sheet.cells || {};
  const rows = Math.min(sheet.rows || 30, 60);
  const cols = Math.min(sheet.cols || 10, 14);
  let html = '<table style="border-collapse:collapse;width:100%;font-size:13px">';
  for (let r = 0; r < rows; r += 1) {
    let hasContent = false;
    let rowHtml = '';
    for (let c = 0; c < cols; c += 1) {
      const val = cells[`${r},${c}`] || '';
      if (val) hasContent = true;
      rowHtml += `<td style="border:1px solid #ddd;padding:4px 6px">${esc(val)}</td>`;
    }
    if (hasContent) html += `<tr>${rowHtml}</tr>`;
  }
  html += '</table>';
  return html;
}

/** Rend une présentation en sections (titre + contenu). */
function pptToHtml(content: Record<string, unknown>): string {
  const slides = (content.slides as Array<{ title?: string; body?: string }> | undefined) || [];
  return slides
    .map((s) => `<h3 style="margin:16px 0 6px">${esc(s.title || '')}</h3><p style="margin:0 0 12px">${esc(s.body || '').replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function shareItemFromDoc(doc: DocDocument): ShareItem {
  const c = (doc.content || {}) as Record<string, unknown>;
  if (doc.doc_type === 'excel') {
    // `content` est conservé pour que la page publique /preview puisse rendre
    // un vrai tableau (largeurs, formats) et pas seulement du HTML figé.
    return { name: doc.name, type: 'excel', html: excelToHtml(c), content: c };
  }
  if (doc.doc_type === 'powerpoint') {
    const deck = c.deck && typeof c.deck === 'object' ? c.deck : null;
    if (deck) return { name: doc.name, type: 'presentation', html: pptToHtml(c), deck, content: c };
    return { name: doc.name, type: 'powerpoint', html: pptToHtml(c), content: c };
  }
  if (doc.doc_type === 'pdf') {
    const pdfBase64 = typeof c.pdfBase64 === 'string' ? c.pdfBase64 : '';
    return { name: doc.name, type: 'pdf', pdfBase64 };
  }
  const html = typeof c.html === 'string' ? c.html : '';
  return { name: doc.name, type: 'word', html, content: c };
}

/** Ensemble d'un dossier + tous ses sous-dossiers (récursif). */
function folderScopeOf(rootId: number, folders: DocFolder[]): Set<number> {
  const ids = new Set<number>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    folders.forEach((f) => {
      if (f.parent_id != null && ids.has(f.parent_id) && !ids.has(f.id)) {
        ids.add(f.id);
        changed = true;
      }
    });
  }
  return ids;
}

/**
 * Crée un PARTAGE PUBLIC (lien + mot de passe facultatif) d'un ou plusieurs
 * documents / dossiers. Le contenu est figé au moment du partage : n'importe
 * qui possédant le lien peut le consulter, avec ou sans compte Zifek.
 *
 * Mode `folder` : le dossier est partagé SEUL — tout son contenu (fichiers et
 * sous-dossiers) devient accessible via le lien, sans avoir à cocher les
 * fichiers un par un.
 */
export default function DocsShareModal({
  userId, userName, title, roomId, initialFolderId, initialDocIds, mode, onClose,
}: Props) {
  const lockedMode: 'select' | 'folder' | 'doc' = mode
    || (initialFolderId != null ? 'folder' : (initialDocIds && initialDocIds.length === 1 ? 'doc' : 'select'));

  const [folders, setFolders] = useState<DocFolder[]>([]);
  const [documents, setDocuments] = useState<DocDocument[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<Set<number>>(
    new Set(lockedMode === 'select' && initialFolderId != null ? [initialFolderId] : []),
  );
  const [selectedDocs, setSelectedDocs] = useState<Set<number>>(
    new Set(lockedMode === 'select' ? (initialDocIds || []) : []),
  );
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [f, d] = await Promise.all([fetchFolders(userId), fetchDocuments(userId)]);
        if (active) { setFolders(f); setDocuments(d); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [userId]);

  /** Identifiants des dossiers sélectionnés + leurs sous-dossiers. */
  const folderScope = useMemo(() => {
    const ids = new Set<number>(selectedFolders);
    let changed = true;
    while (changed) {
      changed = false;
      folders.forEach((f) => {
        if (f.parent_id != null && ids.has(f.parent_id) && !ids.has(f.id)) { ids.add(f.id); changed = true; }
      });
    }
    return ids;
  }, [selectedFolders, folders]);

  /** Dossier verrouillé : lui + tous ses sous-dossiers. */
  const lockedFolderScope = useMemo(
    () => (lockedMode === 'folder' && initialFolderId != null
      ? folderScopeOf(initialFolderId, folders)
      : new Set<number>()),
    [lockedMode, initialFolderId, folders],
  );

  const lockedFolder = useMemo(
    () => (initialFolderId != null ? folders.find((f) => f.id === initialFolderId) || null : null),
    [initialFolderId, folders],
  );

  const lockedDoc = useMemo(
    () => (lockedMode === 'doc' && initialDocIds && initialDocIds.length > 0
      ? documents.find((d) => d.id === initialDocIds[0]) || null
      : null),
    [lockedMode, initialDocIds, documents],
  );

  /** Documents réellement inclus dans un partage de dossier (tout le contenu). */
  const lockedFolderDocs = useMemo(
    () => documents.filter((d) => d.folder_id != null && lockedFolderScope.has(d.folder_id)),
    [documents, lockedFolderScope],
  );

  const effectiveDocs = useMemo(() => {
    const set = new Map<number, DocDocument>();
    documents.forEach((d) => {
      if (selectedDocs.has(d.id)) set.set(d.id, d);
      else if (d.folder_id != null && folderScope.has(d.folder_id)) set.set(d.id, d);
    });
    return Array.from(set.values());
  }, [documents, selectedDocs, folderScope]);

  const rootSelected = selectedFolders.has(-1);

  const toggleFolder = (id: number) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDoc = (id: number) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildItems = (): ShareItem[] => {
    if (lockedMode === 'folder') return lockedFolderDocs.map(shareItemFromDoc);
    if (lockedMode === 'doc') return lockedDoc ? [shareItemFromDoc(lockedDoc)] : [];
    return (rootSelected ? documents : effectiveDocs).map(shareItemFromDoc);
  };

  const create = async () => {
    setError(null);
    const items = buildItems();
    if (items.length === 0) {
      setError(
        lockedMode === 'folder'
          ? 'Ce dossier ne contient aucun fichier à partager.'
          : 'Sélectionnez au moins un document ou un dossier.',
      );
      return;
    }
    setBusy(true);
    const passwordHash = password.trim() ? md5(password.trim()) : null;
    const shareTitle = lockedMode === 'folder' && lockedFolder ? lockedFolder.name : title;
    const share = await createShare(
      userId,
      shareTitle,
      items,
      passwordHash,
      lockedMode === 'folder' ? 'folder' : lockedMode === 'doc' ? 'doc' : 'docs',
    );
    if (!share) {
      setBusy(false);
      setError('Échec de création du partage. Réessayez.');
      return;
    }
    const absolute = shareAbsoluteUrl(share.token);
    if (roomId) {
      await addRoomFile(roomId, {
        userId,
        userName,
        name: `${shareTitle}${password.trim() ? ' (protégé)' : ''}`,
        url: absolute,
        kind: 'link',
      });
    }
    setLink(absolute);
    setBusy(false);
  };

  const copy = () => {
    if (link) void navigator.clipboard.writeText(link);
  };

  const shareNative = async () => {
    if (!link) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title, url: link }); } catch { /* annulé */ }
    } else {
      copy();
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl p-6 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold font-heading text-foreground-950">Partager des documents</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {roomId ? 'Ces documents seront accessibles pendant la réunion.' : 'Générez un lien public de consultation.'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {link ? (
          <div className="space-y-4 overflow-y-auto">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
              <i className="ri-check-line text-xl text-emerald-500"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800 mb-2">Lien de partage créé</p>
              <div className="flex items-center gap-2">
                <input readOnly value={link} className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs font-mono text-foreground-700" />
                <button onClick={copy} className="px-4 py-2 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium cursor-pointer hover:bg-secondary-100 whitespace-nowrap">
                  <i className="ri-file-copy-line mr-1"></i>Copier
                </button>
              </div>
              {password.trim() && (
                <p className="text-[11px] text-foreground-500 mt-2 flex items-center gap-1">
                  <i className="ri-lock-line"></i>Mot de passe requis : <strong>{password.trim()}</strong>
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowQr(true)}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200/70 transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-qr-code-line"></i>QR code
              </button>
              <button
                onClick={shareNative}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-share-forward-line"></i>Partager
              </button>
            </div>

            {showQr && (
              <div className="flex flex-col items-center gap-2 py-3 bg-background-100 rounded-xl">
                <div className="p-3 bg-white rounded-xl border border-background-200/70">
                  <QRCodeSVG value={link} size={170} level="H" fgColor="#172C43" bgColor="#ffffff" />
                </div>
                <p className="text-[11px] text-foreground-500">Scannez pour ouvrir ce partage</p>
              </div>
            )}

            <button onClick={onClose} className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-primary-600 whitespace-nowrap">
              Terminer
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {lockedMode === 'folder' && (
                <div className="rounded-xl border border-accent-200 bg-accent-50/60 p-4">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-folder-3-fill text-xl"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{lockedFolder?.name || title}</p>
                      <p className="text-xs text-foreground-500">
                        {lockedFolderDocs.length} fichier{lockedFolderDocs.length !== 1 ? 's' : ''} inclus
                        {lockedFolderScope.size > 1 ? ` · ${lockedFolderScope.size} sous-dossiers` : ''}
                      </p>
                    </div>
                  </div>
                  <p className="text-[11px] text-foreground-500 mt-3 flex items-start gap-1.5">
                    <i className="ri-information-line mt-0.5"></i>
                    Le lien donne accès à tout le contenu de ce dossier (fichiers et sous-dossiers), sans compte Zifek.
                  </p>
                </div>
              )}

              {lockedMode === 'doc' && lockedDoc && (
                <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-4">
                  <div className="flex items-center gap-3">
                    <span className={`w-10 h-10 rounded-lg bg-background-50 flex items-center justify-center flex-shrink-0`}>
                      <i className={`${(docTypeMeta[lockedDoc.doc_type] || docTypeMeta.word).icon} text-lg ${(docTypeMeta[lockedDoc.doc_type] || docTypeMeta.word).iconClass}`}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{lockedDoc.name}</p>
                      <p className="text-xs text-foreground-500">Seul ce fichier sera partagé.</p>
                    </div>
                  </div>
                </div>
              )}

              {lockedMode === 'select' && (
                <>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-2">Dossiers</p>
                    <div className="space-y-1">
                      <label className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer">
                        <input type="checkbox" checked={rootSelected} onChange={() => toggleFolder(-1)} className="cursor-pointer" />
                        <i className="ri-folders-line text-accent-500"></i>
                        <span className="text-sm text-foreground-700">Tous mes documents</span>
                      </label>
                      {folders.map((f) => (
                        <label key={f.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer">
                          <input type="checkbox" checked={selectedFolders.has(f.id)} onChange={() => toggleFolder(f.id)} className="cursor-pointer" />
                          <i className="ri-folder-3-fill text-accent-500"></i>
                          <span className="text-sm text-foreground-700 truncate">{f.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-2">Fichiers</p>
                    <div className="space-y-1">
                      {documents.map((d) => {
                        const meta = docTypeMeta[d.doc_type] || docTypeMeta.word;
                        const included = selectedDocs.has(d.id) || (d.folder_id != null && folderScope.has(d.folder_id)) || rootSelected;
                        return (
                          <label key={d.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer">
                            <input type="checkbox" checked={included} onChange={() => toggleDoc(d.id)} className="cursor-pointer" />
                            <i className={`${meta.icon} ${meta.iconClass}`}></i>
                            <span className="text-sm text-foreground-700 truncate">{d.name}</span>
                          </label>
                        );
                      })}
                      {documents.length === 0 && (
                        <p className="text-xs text-foreground-400 px-3 py-2">Aucun document disponible.</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Mot de passe (facultatif)</label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Laisser vide pour un accès libre"
                  className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            {error && <p className="text-xs text-red-500 mt-3">{error}</p>}

            <button
              onClick={create}
              disabled={busy}
              className="mt-4 w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {busy ? 'Création…' : 'Créer le lien de partage'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}